import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiRequest } from "../../api/client";

export const fetchGroceryLists = createAsyncThunk(
  "groceryLists/fetchGroceryLists",
  async ({ force } = {}, thunkApi) => {
    const res = await apiRequest("api/grocery_lists", { method: "GET" });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Failed to load grocery lists."
      );
    }

    return Array.isArray(res.data) ? res.data : [];
  },
  {
    condition: ({ force } = {}, { getState }) => {
      const status = getState()?.groceryLists?.fetchStatus;
      // Prevent duplicate calls (e.g. React StrictMode double-effects)
      if (force) return true;
      if (status === "loading" || status === "succeeded") return false;
      return true;
    },
  }
);

export const createGroceryList = createAsyncThunk(
  "groceryLists/createGroceryList",
  async ({ title, budget }, thunkApi) => {
    const res = await apiRequest("api/grocery_lists", {
      method: "POST",
      body: { title, budget },
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Failed to create grocery list."
      );
    }

    return res.data;
  }
);

export const fetchGroceryListDetail = createAsyncThunk(
  "groceryLists/fetchGroceryListDetail",
  async ({ id }, thunkApi) => {
    const res = await apiRequest(`api/grocery_lists/${id}`, { method: "GET" });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Failed to load grocery list."
      );
    }

    return res.data;
  },
  {
    condition: ({ id, force } = {}, { getState }) => {
      const s = getState()?.groceryLists;
      if (!id) return false;
      if (force) return true;

      // Skip duplicate in-flight requests
      if (s?.detailStatus === "loading") return false;

      // If we already have this detail loaded, don't refetch
      if (s?.detailStatus === "succeeded" && s?.detail?.id === id) return false;

      return true;
    },
  }
);

export const updateGroceryList = createAsyncThunk(
  "groceryLists/updateGroceryList",
  async ({ id, title, budget }, thunkApi) => {
    const res = await apiRequest(`api/grocery_lists/${id}`, {
      method: "PUT",
      body: { title, budget },
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Failed to update grocery list."
      );
    }

    return { id, data: res.data };
  }
);

export const deleteGroceryList = createAsyncThunk(
  "groceryLists/deleteGroceryList",
  async ({ id }, thunkApi) => {
    const res = await apiRequest(`api/grocery_lists/${id}`, { method: "DELETE" });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Failed to delete grocery list."
      );
    }

    return { id };
  }
);

export const addGroceryProduct = createAsyncThunk(
  "groceryLists/addGroceryProduct",
  async (
    { grocery_list_id, product_id, image_url, price, weight, quantity, product_name, platform },
    thunkApi
  ) => {
    const body = {
      product_id,
      image_url,
      price,
      weight,
      quantity,
      ...(product_name ? { product_name } : {}),
      ...(platform ? { platform } : {}),
    };
    const res = await apiRequest(
      `api/grocery_lists/${grocery_list_id}/grocery_products`,
      {
        method: "POST",
        body,
      }
    );

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Failed to add product to list."
      );
    }

    return { grocery_list_id, data: res.data };
  }
);

export const updateGroceryProduct = createAsyncThunk(
  "groceryLists/updateGroceryProduct",
  async (
    {
      grocery_list_id,
      id,
      product_id,
      image_url,
      price,
      weight,
      quantity,
      product_name,
      platform,
    },
    thunkApi
  ) => {
    const body = {
      product_id,
      image_url,
      price,
      weight,
      quantity,
      ...(product_name ? { product_name } : {}),
      ...(platform ? { platform } : {}),
    };
    const res = await apiRequest(
      `api/grocery_lists/${grocery_list_id}/grocery_products/${id}`,
      {
        method: "PUT",
        body,
      }
    );

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Failed to update product."
      );
    }

    return { grocery_list_id, id, data: res.data };
  }
);

export const deleteGroceryProduct = createAsyncThunk(
  "groceryLists/deleteGroceryProduct",
  async ({ grocery_list_id, id }, thunkApi) => {
    const res = await apiRequest(
      `api/grocery_lists/${grocery_list_id}/grocery_products/${id}`,
      { method: "DELETE" }
    );

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Failed to delete product."
      );
    }

    return { grocery_list_id, id };
  }
);

const initialState = {
  items: [],
  fetchStatus: "idle", // idle | loading | succeeded | failed
  fetchError: "",
  createStatus: "idle", // idle | loading | succeeded | failed
  createError: "",
  detail: null,
  detailStatus: "idle", // idle | loading | succeeded | failed
  detailError: "",
  updateStatus: "idle", // idle | loading | succeeded | failed
  updateError: "",
  deleteStatus: "idle", // idle | loading | succeeded | failed
  deleteError: "",
  addProductStatus: "idle", // idle | loading | succeeded | failed
  addProductError: "",
  updateProductStatus: "idle", // idle | loading | succeeded | failed
  updateProductError: "",
  deleteProductStatus: "idle", // idle | loading | succeeded | failed
  deleteProductError: "",
};

const groceryListsSlice = createSlice({
  name: "groceryLists",
  initialState,
  reducers: {
    clearGroceryLists(state) {
      state.items = [];
      state.fetchStatus = "idle";
      state.fetchError = "";
      state.createStatus = "idle";
      state.createError = "";
      state.detail = null;
      state.detailStatus = "idle";
      state.detailError = "";
      state.updateStatus = "idle";
      state.updateError = "";
      state.deleteStatus = "idle";
      state.deleteError = "";
      state.addProductStatus = "idle";
      state.addProductError = "";
      state.updateProductStatus = "idle";
      state.updateProductError = "";
      state.deleteProductStatus = "idle";
      state.deleteProductError = "";
    },
    clearCreateGroceryListState(state) {
      state.createStatus = "idle";
      state.createError = "";
    },
    clearGroceryListDetailState(state) {
      state.detail = null;
      state.detailStatus = "idle";
      state.detailError = "";
    },
    clearUpdateGroceryListState(state) {
      state.updateStatus = "idle";
      state.updateError = "";
    },
    clearDeleteGroceryListState(state) {
      state.deleteStatus = "idle";
      state.deleteError = "";
    },
    clearAddGroceryProductState(state) {
      state.addProductStatus = "idle";
      state.addProductError = "";
    },
    clearUpdateGroceryProductState(state) {
      state.updateProductStatus = "idle";
      state.updateProductError = "";
    },
    clearDeleteGroceryProductState(state) {
      state.deleteProductStatus = "idle";
      state.deleteProductError = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroceryLists.pending, (state) => {
        state.fetchStatus = "loading";
        state.fetchError = "";
      })
      .addCase(fetchGroceryLists.fulfilled, (state, action) => {
        state.fetchStatus = "succeeded";
        state.items = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchGroceryLists.rejected, (state, action) => {
        state.fetchStatus = "failed";
        state.fetchError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to load grocery lists.";
      })
      .addCase(createGroceryList.pending, (state) => {
        state.createStatus = "loading";
        state.createError = "";
      })
      .addCase(createGroceryList.fulfilled, (state, action) => {
        state.createStatus = "succeeded";
        state.createError = "";
        // API may return the created object; if not, keep list consistent by refetching later.
        if (action.payload && typeof action.payload === "object") {
          const created = action.payload;
          const exists = state.items?.some?.((i) => i?.id === created?.id);
          if (!exists) state.items = [created, ...(state.items || [])];
        }
      })
      .addCase(createGroceryList.rejected, (state, action) => {
        state.createStatus = "failed";
        state.createError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to create grocery list.";
      })
      .addCase(fetchGroceryListDetail.pending, (state) => {
        state.detailStatus = "loading";
        state.detailError = "";
      })
      .addCase(fetchGroceryListDetail.fulfilled, (state, action) => {
        state.detailStatus = "succeeded";
        state.detail = action.payload || null;
      })
      .addCase(fetchGroceryListDetail.rejected, (state, action) => {
        state.detailStatus = "failed";
        state.detailError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to load grocery list.";
      })
      .addCase(updateGroceryList.pending, (state) => {
        state.updateStatus = "loading";
        state.updateError = "";
      })
      .addCase(updateGroceryList.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";
        state.updateError = "";
        const id = action.payload?.id;
        const updated = action.payload?.data;
        if (id != null && updated && typeof updated === "object") {
          // Update list card data if present
          state.items = (state.items || []).map((it) =>
            it?.id === id ? { ...it, ...updated } : it
          );
          // Update detail if open
          if (state.detail?.id === id) state.detail = { ...state.detail, ...updated };
        }
      })
      .addCase(updateGroceryList.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to update grocery list.";
      })
      .addCase(deleteGroceryList.pending, (state) => {
        state.deleteStatus = "loading";
        state.deleteError = "";
      })
      .addCase(deleteGroceryList.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        state.deleteError = "";
        const id = action.payload?.id;
        if (id != null) {
          state.items = (state.items || []).filter((it) => it?.id !== id);
          if (state.detail?.id === id) {
            state.detail = null;
            state.detailStatus = "idle";
          }
        }
      })
      .addCase(deleteGroceryList.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.deleteError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to delete grocery list.";
      })
      .addCase(addGroceryProduct.pending, (state) => {
        state.addProductStatus = "loading";
        state.addProductError = "";
      })
      .addCase(addGroceryProduct.fulfilled, (state) => {
        state.addProductStatus = "succeeded";
        state.addProductError = "";
      })
      .addCase(addGroceryProduct.rejected, (state, action) => {
        state.addProductStatus = "failed";
        state.addProductError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to add product to list.";
      })
      .addCase(updateGroceryProduct.pending, (state) => {
        state.updateProductStatus = "loading";
        state.updateProductError = "";
      })
      .addCase(updateGroceryProduct.fulfilled, (state, action) => {
        state.updateProductStatus = "succeeded";
        state.updateProductError = "";

        const id = action.payload?.id;
        const updated = action.payload?.data;
        if (id == null || !updated || !state.detail) return;

        const productArrayKeys = ["products", "items", "grocery_list_products", "grocery_products"];
        productArrayKeys.forEach((key) => {
          if (!Array.isArray(state.detail?.[key])) return;
          state.detail[key] = state.detail[key].map((item) =>
            item?.id === id ? { ...item, ...updated } : item
          );
        });
      })
      .addCase(updateGroceryProduct.rejected, (state, action) => {
        state.updateProductStatus = "failed";
        state.updateProductError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to update product.";
      })
      .addCase(deleteGroceryProduct.pending, (state) => {
        state.deleteProductStatus = "loading";
        state.deleteProductError = "";
      })
      .addCase(deleteGroceryProduct.fulfilled, (state, action) => {
        state.deleteProductStatus = "succeeded";
        state.deleteProductError = "";

        const id = action.payload?.id;
        if (id == null || !state.detail) return;

        const productArrayKeys = ["products", "items", "grocery_list_products", "grocery_products"];
        productArrayKeys.forEach((key) => {
          if (!Array.isArray(state.detail?.[key])) return;
          state.detail[key] = state.detail[key].filter((item) => item?.id !== id);
        });
      })
      .addCase(deleteGroceryProduct.rejected, (state, action) => {
        state.deleteProductStatus = "failed";
        state.deleteProductError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to delete product.";
      });
  },
});

export const {
  clearGroceryLists,
  clearCreateGroceryListState,
  clearGroceryListDetailState,
  clearUpdateGroceryListState,
  clearDeleteGroceryListState,
  clearAddGroceryProductState,
  clearUpdateGroceryProductState,
  clearDeleteGroceryProductState,
} = groceryListsSlice.actions;
export default groceryListsSlice.reducer;

